import { useEffect, useRef } from 'react'

type Props = {
  siteKey: string
  onToken: (token: string) => void
  onExpire: () => void
}

/** Widget de Cloudflare Turnstile — ver backend/app/security/turnstile.py
 * para la verificación del lado del servidor. `siteKey` vacío deshabilita
 * el widget entero, mismo criterio que usa el backend con
 * `TURNSTILE_SECRET_KEY` vacío. */
export function TurnstileWidget({ siteKey, onToken, onExpire }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  // `onToken`/`onExpire` llegan como funciones inline desde LoginPage —
  // referencia nueva en cada render suyo (p. ej. al tipear en cualquier
  // campo, o apenas este mismo widget llama a `onToken` y el padre hace
  // `setState`). Guardarlas en un ref, en vez de en las dependencias del
  // efecto de abajo, evita que cada re-render del padre tire abajo
  // (`remove()`) y vuelva a montar el widget a mitad de una verificación —
  // eso es lo que se veía como "se tilda y se destilda solo".
  const onTokenRef = useRef(onToken)
  const onExpireRef = useRef(onExpire)
  onTokenRef.current = onToken
  onExpireRef.current = onExpire

  useEffect(() => {
    if (!siteKey) return
    let widgetId: string | undefined

    function render() {
      if (!containerRef.current || !window.turnstile) return
      widgetId = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onExpireRef.current(),
        'error-callback': () => onExpireRef.current(),
      })
    }

    if (window.turnstile) {
      render()
    } else {
      // El script se carga una sola vez — si el componente vuelve a montar
      // (el `key` cambia en LoginPage para pedir un token nuevo), un
      // <script> ya presente en el <head> no se duplica.
      const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
      const script = existing ?? document.createElement('script')
      if (!existing) {
        script.setAttribute('data-turnstile', '')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
        script.async = true
        document.head.appendChild(script)
      }
      script.addEventListener('load', render, { once: true })
    }

    return () => {
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId)
    }
    // Deliberadamente sólo `siteKey`: ver el comentario de los refs arriba.
  }, [siteKey])

  if (!siteKey) return null
  return <div ref={containerRef} />
}
